import { useMemo, useState } from 'react';
import { ChevronDown, Loader2 } from 'lucide-react';
import { InputSwitch } from 'primereact/inputswitch';
import { useDispatch } from 'react-redux';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from 'src/components/ui/collapsible';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';
import { PortalDsButton } from 'src/components/portal-design-system';
import { formatDisplayDateTime } from 'src/helpers/date.helpers';
import { formatScheduleForDisplay } from 'src/helpers/scheduler-schedule.helpers';
import { formatSchedulerMutationError } from 'src/helpers/scheduler-schedule.helpers';
import { cn } from 'src/lib/utils';
import { showNotification } from 'src/redux/notification/notification.slice';
import {
  useRunClientDataTransferNowMutation,
  useUpdateClientDataTransferMutation,
} from 'src/redux/operations/operations.endpoints';
import {
  formatRowCount,
  groupTransfersByClient,
} from '../helpers/data-transfers.helpers';
import type { ClientDataTransfer } from '../types/data-transfers.types';
import { DataTransferRunNowDialog } from './data-transfer-run-now-dialog';
import { DataTransferStatusBadge } from './data-transfer-status-badge';

interface ClientTransfersByClientListProps {
  transfers: ClientDataTransfer[];
  isLoading: boolean;
  onSelectTransfer: (transfer: ClientDataTransfer) => void;
  onEditTransfer: (transfer: ClientDataTransfer) => void;
}

export function ClientTransfersByClientList({
  transfers,
  isLoading,
  onSelectTransfer,
  onEditTransfer,
}: ClientTransfersByClientListProps) {
  const dispatch = useDispatch();
  const [runNowTransfer, setRunNowTransfer] = useState<ClientDataTransfer | null>(null);
  const [togglingTransferId, setTogglingTransferId] = useState<number | null>(null);
  const [openClients, setOpenClients] = useState<Record<string, boolean>>({});

  const [updateTransfer] = useUpdateClientDataTransferMutation();
  const [runNow, { isLoading: isRunningNow }] = useRunClientDataTransferNowMutation();

  const groupedTransfers = useMemo(
    () => groupTransfersByClient(transfers),
    [transfers]
  );

  const handleToggleEnabled = async(
    transfer: ClientDataTransfer,
    enabled: boolean
  ) => {
    setTogglingTransferId(transfer.id);
    try {
      await updateTransfer({
        transferId: transfer.id,
        body: { is_enabled: enabled },
      }).unwrap();
      dispatch(
        showNotification({
          summary: enabled
            ? `Enabled "${transfer.name}"`
            : `Paused "${transfer.name}"`,
          theme: 'success',
        })
      );
    } catch (error) {
      dispatch(
        showNotification({
          summary: formatSchedulerMutationError(error),
          theme: 'warning',
        })
      );
    } finally {
      setTogglingTransferId(null);
    }
  };

  const handleConfirmRunNow = async() => {
    if (!runNowTransfer) {
      return;
    }
    try {
      await runNow({ transferId: runNowTransfer.id }).unwrap();
      dispatch(
        showNotification({
          summary: `Started manual run for "${runNowTransfer.name}"`,
          theme: 'success',
        })
      );
      setRunNowTransfer(null);
    } catch (error) {
      dispatch(
        showNotification({
          summary: formatSchedulerMutationError(error),
          theme: 'warning',
        })
      );
    }
  };

  if (isLoading) {
    return (
      <div
        className="flex items-center gap-2 py-8 text-sm text-gray-500"
        role="status"
      >
        <Loader2 className="size-4 animate-spin" />
        Loading transfers…
      </div>
    );
  }

  if (!groupedTransfers.length) {
    return (
      <p className="py-8 text-center text-sm text-gray-500">
        No client data transfers yet.
      </p>
    );
  }

  return (
    <>
      <div className="space-y-4">
        {groupedTransfers.map(({ clientTitle, transfers: clientTransfers }) => {
          const isOpen = openClients[clientTitle] ?? true;

          return (
            <Collapsible
              key={clientTitle}
              open={isOpen}
              onOpenChange={(open) => {
                setOpenClients((current) => ({ ...current, [clientTitle]: open }));
              }}
            >
              <div className="overflow-hidden rounded-md border border-gray-400">
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    className="flex w-full items-center justify-between gap-3 bg-gray-200 px-4 py-3 text-left transition-colors hover:bg-gray-200/80"
                  >
                    <div>
                      <p className="text-sm font-medium text-black">
                        {clientTitle}
                      </p>
                      <p className="text-xs text-gray-600">
                        {clientTransfers.length}
                        {' '}
                        transfer
                        {clientTransfers.length === 1 ? '' : 's'}
                      </p>
                    </div>
                    <ChevronDown
                      className={cn(
                        'size-4 shrink-0 text-gray-600 transition-transform',
                        isOpen && 'rotate-180'
                      )}
                    />
                  </button>
                </CollapsibleTrigger>

                <CollapsibleContent>
                  <Table>
                    <TableHeader>
                      <TableRow className="border-gray-400 bg-white hover:bg-white">
                        <TableHead className="px-4 py-3 text-sm font-medium text-gray-700">
                          Name
                        </TableHead>
                        <TableHead className="px-4 py-3 text-sm font-medium text-gray-700">
                          Schedule
                        </TableHead>
                        <TableHead className="px-4 py-3 text-sm font-medium text-gray-700">
                          Status
                        </TableHead>
                        <TableHead className="px-4 py-3 text-sm font-medium text-gray-700">
                          Last run
                        </TableHead>
                        <TableHead className="px-4 py-3 text-sm font-medium text-gray-700">
                          Next run
                        </TableHead>
                        <TableHead className="px-4 py-3 text-sm font-medium text-gray-700">
                          Rows
                        </TableHead>
                        <TableHead className="px-4 py-3 text-sm font-medium text-gray-700">
                          Enabled
                        </TableHead>
                        <TableHead className="px-4 py-3 text-right text-sm font-medium text-gray-700">
                          Actions
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {clientTransfers.map((transfer) => (
                        <TableRow
                          key={transfer.id}
                          className={cn(
                            'border-b border-gray-300 align-top',
                            !transfer.is_enabled && 'opacity-60'
                          )}
                        >
                          <TableCell className="px-4 py-3">
                            <button
                              type="button"
                              className="text-left text-sm font-medium text-black hover:underline"
                              onClick={() => onSelectTransfer(transfer)}
                            >
                              {transfer.name}
                            </button>
                          </TableCell>
                          <TableCell className="px-4 py-3 font-mono text-xs text-gray-700">
                            {formatScheduleForDisplay(transfer)}
                          </TableCell>
                          <TableCell className="px-4 py-3">
                            <DataTransferStatusBadge
                              status={transfer.last_status}
                              disabled={!transfer.is_enabled}
                            />
                          </TableCell>
                          <TableCell className="px-4 py-3 text-sm text-gray-700">
                            {transfer.last_run_at
                              ? formatDisplayDateTime(transfer.last_run_at)
                              : '—'}
                          </TableCell>
                          <TableCell className="px-4 py-3 text-sm text-gray-700">
                            {transfer.is_enabled && transfer.next_run_at
                              ? formatDisplayDateTime(transfer.next_run_at)
                              : '—'}
                          </TableCell>
                          <TableCell className="px-4 py-3 text-sm text-gray-700">
                            {formatRowCount(transfer.last_row_count)}
                          </TableCell>
                          <TableCell className="px-4 py-3">
                            <InputSwitch
                              checked={transfer.is_enabled}
                              disabled={togglingTransferId === transfer.id}
                              onChange={(event) => {
                                void handleToggleEnabled(transfer, event.value);
                              }}
                              onClick={(event) => event.stopPropagation()}
                            />
                          </TableCell>
                          <TableCell className="px-4 py-3 text-right">
                            <div className="flex justify-end gap-2">
                              <PortalDsButton
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => onEditTransfer(transfer)}
                              >
                                Edit
                              </PortalDsButton>
                              <PortalDsButton
                                type="button"
                                size="sm"
                                disabled={!transfer.is_enabled}
                                onClick={() => setRunNowTransfer(transfer)}
                              >
                                Run now
                              </PortalDsButton>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CollapsibleContent>
              </div>
            </Collapsible>
          );
        })}
      </div>

      <DataTransferRunNowDialog
        transfer={runNowTransfer}
        open={runNowTransfer !== null}
        isRunning={isRunningNow}
        onOpenChange={(open) => {
          if (!open) {
            setRunNowTransfer(null);
          }
        }}
        onConfirm={handleConfirmRunNow}
      />
    </>
  );
}
